// Centralised resolver for the localized Exercise.name (added when the
// schema flipped from plain `string` to `LocalizedText` in shared 1.95.0).
// Reduces boilerplate in the ~10 mobile callsites that render or search
// over catalog rows — they just call `useExerciseName()` once at the top
// of the component and apply the returned function wherever they used
// to read `ex.name` directly.
//
// Search/sort callers that need a STABLE key (independent of locale)
// should reach for `.name.en` directly — the catalog dedup + sort keys
// must not shift when the user toggles language at runtime.

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { catalogBySlug, pickLocalizedText, type Exercise, type LocalizedText, type Locale } from '@jackedlogs/shared';

const SUPPORTED_LOCALES: ReadonlySet<Locale> = new Set([
  'en',
  'it',
  'fr',
  'de',
  'ru',
]);

const normaliseLocale = (raw: string): Locale => {
  // i18next gives `fr` or `fr-CH`; strip the region tag and clamp to
  // the five-locale set the schema supports.
  const head = (raw.split('-')[0] ?? 'en').toLowerCase() as Locale;
  return SUPPORTED_LOCALES.has(head) ? head : 'en';
};

export function useExerciseName(): (name: LocalizedText) => string {
  const { i18n } = useTranslation();
  const locale = normaliseLocale(i18n.language);
  return (name) => pickLocalizedText(name, locale);
}

// Hook variant that resolves an exercise BY SLUG against the active
// catalog. The architectural rule (Antenore 2026-05-22): exercise names
// are NEVER read from write-time snapshot fields; always resolved via
// foreign key + the live catalog. When the catalog row is missing
// (deleted exercise) the helper falls back to the optional snapshot or
// a localised "Deleted exercise" placeholder. Single source of truth =
// the catalog; rename a row and every historical surface picks up the
// new label without a migration.
export function useExerciseNameBySlug(
  catalog: ReadonlyMap<string, Exercise> | ReadonlyArray<Exercise> | null | undefined,
): (slug: string, snapshotFallback?: string) => string {
  const { i18n, t } = useTranslation();
  const locale = normaliseLocale(i18n.language);
  // Memo the catalog Map identity so callers can safely include the
  // returned closure in useMemo/useEffect deps. Re-running when the
  // raw catalog reference changes is enough; rebuilding on every
  // render would force every consumer's downstream memos to bust.
  const map = useMemo<ReadonlyMap<string, Exercise> | null>(() => {
    if (catalog instanceof Map) return catalog;
    if (Array.isArray(catalog)) return catalogBySlug(catalog);
    return null;
  }, [catalog]);
  return useCallback(
    (slug, snapshotFallback) => {
      const ex = map?.get(slug);
      if (ex) return pickLocalizedText(ex.name, locale);
      if (snapshotFallback && snapshotFallback.trim().length > 0)
        return snapshotFallback;
      return t('catalog.deletedExercise', {
        defaultValue: 'Deleted exercise',
        slug,
      });
    },
    [map, locale, t],
  );
}
